import { useState, useEffect } from 'react';
import {
    startOfMonth, endOfMonth, startOfWeek, endOfWeek, addMonths, subMonths,
    format, isSameDay, isSameMonth, isWithinInterval, isAfter, isBefore,
} from 'date-fns';
import DynamicIcon from './dynamic-icon';
import { DATE_RANGE_PRESETS, getPresetRange } from '../../lib/date-buckets';

// Range picker for the "Date created" filter: quick ranges down the left, a
// month grid on the right. Click one day for a single day, a second day to
// close a range; a third click starts over.
const DateRangeFilter = ({ value, onApply, onClear, onClose }) => {
    const [range, setRange] = useState(value || { from: null, to: null });
    const [currentMonth, setCurrentMonth] = useState(startOfMonth(value?.from || new Date()));
    const [activePreset, setActivePreset] = useState(null);

    useEffect(() => {
        setRange(value || { from: null, to: null });
        if (value?.from) setCurrentMonth(startOfMonth(value.from));
    }, [value]);

    const applyPreset = (preset) => {
        const next = getPresetRange(preset.id);
        if (!next) return;
        setActivePreset(preset.id);
        setRange(next);
        setCurrentMonth(startOfMonth(next.from));
        onApply(next);
    };

    const handleDayClick = (day) => {
        setActivePreset(null);
        // No start yet, or a complete range already — begin a new one.
        if (!range.from || (range.from && range.to)) {
            setRange({ from: day, to: null });
            return;
        }
        const next = isBefore(day, range.from)
            ? { from: day, to: range.from }
            : { from: range.from, to: day };
        setRange(next);
        onApply(next);
    };

    const inRange = (day) => {
        if (!range.from) return false;
        if (!range.to) return isSameDay(day, range.from);
        return isWithinInterval(day, { start: range.from, end: range.to });
    };

    const renderCells = () => {
        const monthStart = startOfMonth(currentMonth);
        const start = startOfWeek(monthStart);
        const end = endOfWeek(endOfMonth(monthStart));
        const cells = [];
        let day = start;

        while (day <= end) {
            const cloneDay = day;
            const isEdge = (range.from && isSameDay(day, range.from)) || (range.to && isSameDay(day, range.to));
            const isInside = inRange(day) && !isEdge;
            const isToday = isSameDay(day, new Date());
            const isCurrentMonth = isSameMonth(day, monthStart);

            cells.push(
                <div
                    key={day.toISOString()}
                    onClick={() => handleDayClick(cloneDay)}
                    className={`
                        h-[30px] w-full cursor-pointer grid items-center justify-center text-[12px] poppins-medium
                        ${isEdge ? 'custom-bg-grey-500 !custom-text-grey-50 rounded-[8px]' : ''}
                        ${isInside ? 'custom-bg-grey-50 custom-text-grey-900 rounded-[0px]' : ''}
                        ${!isEdge && !isInside ? 'rounded-[8px] hover:custom-bg-grey-50' : ''}
                        ${!isCurrentMonth && !isEdge && !isInside ? 'custom-text-grey-200' : ''}
                        ${isCurrentMonth && !isEdge && !isInside && isToday ? 'custom-text-grey-900 custom-bg-grey-50' : ''}
                        ${isCurrentMonth && !isEdge && !isInside && !isToday ? 'custom-text-grey-900' : ''}
                    `}
                >
                    <span>{format(day, 'd')}</span>
                </div>,
            );
            day = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1);
        }
        return cells;
    };

    return (
        <div className="absolute top-[34px] left-0 z-[1000000] flex custom-bg-white-50 rounded-[8px] custom-shadow overflow-hidden ignore-click">
            <div className="w-[160px] grid content-start gap-[2px] p-[8px] border-r-[1px] custom-border-grey-50">
                {DATE_RANGE_PRESETS.map(preset => (
                    <button
                        key={preset.id}
                        type="button"
                        onClick={() => applyPreset(preset)}
                        className={`text-left px-[10px] py-[7px] rounded-[6px] text-[13px] poppins-regular custom-text-grey-500 hover:custom-bg-grey-50 ${activePreset === preset.id ? 'custom-bg-grey-50' : ''}`}
                    >
                        {preset.label}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={onClear}
                    className="text-left px-[10px] py-[7px] rounded-[6px] text-[13px] poppins-regular custom-text-grey-300 hover:custom-bg-grey-50"
                >
                    Clear
                </button>
            </div>

            <div className="w-[280px] p-[8px]">
                <div className="flex items-center justify-between px-[8px] pb-[8px]">
                    <DynamicIcon
                        name="circle-chevron-left"
                        size={18}
                        color="custom-text-grey-500 cursor-pointer"
                        onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    />
                    <span className="text-[13px] poppins-medium custom-text-grey-900">
                        {format(currentMonth, 'MMMM yyyy')}
                    </span>
                    <DynamicIcon
                        name="circle-chevron-right"
                        size={18}
                        color="custom-text-grey-500 cursor-pointer"
                        onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    />
                </div>

                <div className="grid grid-cols-7 gap-[4px] px-[4px]">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(label => (
                        <div key={label} className="poppins-medium text-[10px] leading-[15px] custom-text-grey-200 text-center uppercase">
                            {label}
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-[4px] px-[4px] pt-[4px]">
                    {renderCells()}
                </div>

                <div className="flex items-center justify-between px-[8px] pt-[10px]">
                    <span className="text-[11px] poppins-regular custom-text-grey-300">
                        {range.from && !range.to ? 'Pick an end date, or the same day again' : ''}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[12px] poppins-medium custom-text-grey-500 px-[10px] py-[4px] rounded-[6px] hover:custom-bg-grey-50"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DateRangeFilter;
